import React, { useState, useEffect, useRef } from "react";
import Main from "./Main";
import AddFund from "../components/AddFund";
import { autoDateFormat } from "../utils/autoDateFormate";
import { fetchAllTransactions } from "../api/transactionApi";
import { formatAmount } from "../utils/formatAmount";

import { useNotify } from "../context/NotifyContext";
import { exportTable } from "../utils/downloadExcel";
import WalletHistoryBar from "../components/WalletHistoryBar";
import {
  ListingFilters,
  ListingPagination,
  SortableTh,
  CopyText,
  AppBreadcrumbs,
  useListingState,
  TXN_STATUS_OPTIONS,
  StatusBadge,
} from "../components/listing";

const LedgerHistory = () => {
  const { notify } = useNotify();
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(null);
  const dropdownRef = useRef(null);
  const listing = useListingState({ sortBy: "date", sortDir: "desc", includeDates: false });
  const [totalPages, setTotalPages] = useState(1);
  const [totalbyall, setTotalbyAll] = useState(0);
  const [members, setTeamMembers] = useState([]);
  const [membersDetails, setTeamDetails] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [totalDebitAmount, setTotalDebitAmount] = useState(0);
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);

  const fetch_ledgers = async () => {
    try {
      const result = await fetchAllTransactions({
        page: listing.page,
        limit: listing.limit,
        search: listing.debouncedSearch || undefined,
        sortBy: listing.sortBy || undefined,
        sortDir: listing.sortBy ? listing.sortDir : undefined,
        status: listing.status || undefined,
      });
      if (result?.success) {
        const response = result?.data;
        setTeamMembers(response?.transactions || []);
        setTeamDetails(response?.userDet);
        setTotalPages(response?.pagination?.totalPages || 1);
        setTotalbyAll(response?.pagination?.total || 0);
        setTotalCreditAmount(response?.totalCredit || 0);
        setTotalDebitAmount(response?.totalDebit || 0);
      } else {
        notify(result?.message || "Server Error", "error");
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetch_ledgers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listing.page,
    listing.limit,
    listing.debouncedSearch,
    listing.sortBy,
    listing.sortDir,
    listing.status,
  ]);

  const handleOpenFund = (member) => {
    setSelectedMember(member);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedMember(null);
    fetch_ledgers();
    setReportOpen(null);
  };

  return (
    <Main showRightPanel={false} showInfoText={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/dashboard" },
            { label: "Teams", to: "/team" },
            { label: "History" },
          ]}
        />

        <WalletHistoryBar to="/main-wallet-ledger">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={`${membersDetails?.logo}`}
              alt="profile"
              className="h-10 w-8 sm:h-10 sm:w-10 object-cover rounded-ds shrink-0"
            />
            <div className="flex items-center gap-2 whitespace-nowrap min-w-0">
              <p className="text-[15px] font-semibold text-[var(--text-dark)]">
                {membersDetails?.name}
              </p>
              <p className="text-[18px] font-semibold text-[var(--text-muted)]">|</p>
              <p className="text-[15px] font-semibold text-[var(--text-muted)]">
                ID: {membersDetails?.userName}
              </p>
            </div>
          </div>
        </WalletHistoryBar>

        <div className="lg:hidden mt-3 flex flex-col gap-3">
          {members?.map((member, index) => (
            <div key={index} className="ds-card">
              <div className="flex flex-col gap-3 text-sm text-[var(--text-dark)]">
                <div>
                  <p className="font-semibold text-[var(--text-muted)]">Transiction Date:</p>
                  <p>{member?.createdAt ? autoDateFormat(member.createdAt, "full") : "N/A"}</p>
                </div>
                <div className="grid grid-cols-2">
                  <div className="flex flex-col items-start">
                    <p className="font-semibold text-[var(--text-muted)]">FromUser:</p>
                    <p>{member?.fromUser?.userName || "Guest"}</p>
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="font-semibold text-[var(--text-muted)]">ToUser:</p>
                    <p>{member?.toUser?.userName || "Guest"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div>
                    <p className="font-semibold text-[var(--text-muted)]">TxtType:</p>
                    <StatusBadge status={member?.type || member?.transactionType} />
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="font-semibold text-[var(--text-muted)]">Status:</p>
                    <StatusBadge status={member?.status} />
                  </div>
                </div>
                <div className="grid grid-cols-2">
                  <div>
                    <p className="font-semibold text-[var(--text-muted)]">Amount:</p>
                    <p>{Number(member?.amount || 0).toFixed(2)}</p>
                  </div>
                  <div className="flex flex-col justify-end items-end">
                    <p className="font-semibold text-[var(--text-muted)]">TxtId:</p>
                    <CopyText value={member?.txnId} label="Txn ID" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => handleOpenFund(member)}
                  className="ds-action-btn"
                >
                  Add Fund
                </button>
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() =>
                      setReportOpen(reportOpen === member?._id ? null : member?._id)
                    }
                    className="ds-action-btn w-full"
                  >
                    Reports ▼
                  </button>
                  {reportOpen === member?._id && (
                    <div className="absolute left-0 mt-1 w-36 bg-white border border-[var(--border-color)] rounded-md shadow-md text-xs z-50 make-text-primary">
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Money Transfer</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Paytm AePS</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Fino AePS</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">Aeps Server -2</p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">
                        RazorPay V1 Add Money
                      </p>
                      <p className="px-3 py-2 hover:bg-gray-100 cursor-pointer">
                        RazorPay V3 Add Money
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalbyall}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[1, 10, 20, 50, 100]}
          />
        </div>

        <div className="ds-card mt-4 hidden lg:block">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex justify-start gap-3">
              <div className="ds-card !p-2 min-w-[150px]">
                <p className="text-[16px] text-[var(--text-dark)]">
                  {Number(totalCreditAmount || 0).toFixed(2)}
                </p>
                <p className="font-semibold text-[var(--text-muted)] text-[13px]">Total Credit</p>
              </div>
              <div className="ds-card !p-2 min-w-[150px]">
                <p className="text-[16px] text-[var(--text-dark)]">
                  {Number(totalDebitAmount || 0).toFixed(2)}
                </p>
                <p className="font-semibold text-[var(--text-muted)] text-[13px]">Total Debit</p>
              </div>
            </div>
          </div>

          <ListingFilters
            onClear={listing.clearFilters}
            showDates={false}
            search={listing.search}
            onSearch={listing.setSearch}
            searchPlaceholder="Search..."
            status={listing.status}
            onStatus={listing.setStatus}
            statusOptions={TXN_STATUS_OPTIONS}
            onExport={() =>
              exportTable(
                "team-ledger-history",
                [
                  { label: "Transaction ID", key: "txId" },
                  { label: "Amount", key: "amount" },
                  { label: "Type", key: "type" },
                  { label: "From", key: "from" },
                  { label: "To", key: "to" },
                  { label: "Status", key: "status" },
                  {
                    label: "Transfer Date",
                    value: (r) => (r?.createdAt ? autoDateFormat(r.createdAt, "full") : ""),
                  },
                ],
                members,
                notify
              )
            }
          />

          <div className="ds-table-wrap ds-scroll">
            <table className="ds-table ds-table-listing min-w-[800px]">
              <thead>
                <tr>
                  <th>TransictionID</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <th>Trns Type</th>
                  <th>From</th>
                  <th>To</th>
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                  <SortableTh
                    label="Transfer Date"
                    sortKey="date"
                    activeKey={listing.sortBy}
                    direction={listing.sortDir}
                    onSort={listing.toggleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {members.map((member, i) => (
                  <tr key={member?._id || i}>
                    <td>
                      <CopyText value={member?.txId} label="Txn ID" />
                    </td>
                    <td>{formatAmount(member?.amount || "_")}</td>
                    <td>
                      <StatusBadge status={member?.type} />
                    </td>
                    <td className="uppercase">{`${member?.from} Wallet` || "-"}</td>
                    <td className="uppercase">{`${member?.to} Wallet` || "-"}</td>
                    <td>
                      <StatusBadge status={member?.status} />
                    </td>
                    <td className="uppercase">
                      {member?.createdAt ? autoDateFormat(member.createdAt, "full") : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ListingPagination
            page={listing.page}
            limit={listing.limit}
            total={totalbyall}
            totalPages={totalPages}
            onPageChange={listing.setPage}
            onLimit={listing.setLimit}
            limitOptions={[1, 10, 20, 50, 100]}
          />
        </div>
      </div>
      {open && <AddFund open={open} close={handleClose} memberData={selectedMember} />}
    </Main>
  );
};

export default LedgerHistory;
